/**
 * The productivity-formula slice of the wire — Task U17 (Tier-3 live-tenant
 * readiness), consuming `GET`/`POST /api/v1/productivity/formulas`,
 * `PATCH /api/v1/productivity/formulas/{id}` and
 * `POST /api/v1/productivity/formulas/{id}/activate`
 * (`src/modules/formulas/routes.ts`, `openapi.yaml`'s `ProductivityFormula`,
 * `ProductivityFormulaCreateRequest` and `ProductivityFormulaPatchRequest`).
 *
 * Hand-written to mirror the contract, the same discipline `policies.ts`,
 * `alertRules.ts` and `retention.ts` state: transcribe what the screen reads, not the
 * whole document.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THIS BACKEND HAS NO CONCURRENCY TOKEN. AT ALL.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `PATCH /productivity/formulas/{id}` and `POST /productivity/formulas/{id}/activate`
 * read no `If-Match`, take no body version and set no `ETag`
 * (`src/modules/formulas/routes.ts:373,387`). There is no compare-and-set to carry,
 * so `preconditionOf` answers `{ kind: 'none' }` for **every** row and the screen says
 * on screen that the later save wins.
 *
 * `null` would be the wrong answer and it is worth saying why, because the two were
 * one value until U14's fix round 1. `null` means *the token is missing* and the hook
 * refuses the write; on a backend that has no token at all, `null` would refuse every
 * write this screen exists to make, silently, inside the hook. `{ kind: 'none' }`
 * means *this backend runs no check* and the request goes out. Nothing here ever
 * returns `null`: a row this screen may not write is a row whose control the screen
 * does not render, which is a different question travelling on a different channel.
 *
 * ── Activation takes no body, which is the one thing that went right ────────
 *
 * The plan told U16–U18 to check what activation takes before trusting the button,
 * after U15 shipped an Activate that governed nothing because U14's `activate` sends
 * `bodyWith({}, precondition)` and `/policies`' activation takes `make_default`. On
 * this backend the answer is that `POST /{id}/activate` reads **no body at all** — the
 * handler takes the id from the path and nothing else (`routes.ts`'s
 * `activateFormulaVersion(database(), access.actor, id)`) — so the empty body the hook
 * sends is exactly the request the contract documents. This is the only one of the
 * four surfaces for which U14's `activate` works unmodified.
 *
 * The *other* activation — "create this version and put it into force in the same
 * transaction" — is `activate: true` on the **create** body, and it is create-only.
 * It is therefore screen state and not a draft key: `ProductivityFormulaPatchRequest`
 * is `additionalProperties: false`, so a draft holding `activate` would put it into
 * the next `PATCH` and earn a `422` naming a field nobody typed. Same for
 * `based_on_formula_id`. U16 found the same class of thing on its own backend.
 *
 * ── The weights are percentage points, and the server is the real gate ──────
 *
 * `API_CONTRACT.md` §6.9 says the positive weights sum to `1.0`; the column, the
 * `components_json` shape and this wire say `100.00`. Same quantity, two spellings —
 * `DATA_MODEL.md` §6395 records the choice. `validateComponents`
 * (`src/modules/formulas/service.ts`) enforces it on **create and on patch**, refusing
 * with `400 WORKFORCE_FORMULA_WEIGHTS_INVALID` and naming the total it computed, and
 * `wp_formula_weights_ck` refuses the row underneath that. So the check in this file
 * is a round trip saved, never the guard: it is deliberately the *same* arithmetic —
 * integer hundredths, at most two decimal places, compared against `10000` — so the
 * form cannot accept a formula the server will refuse, nor refuse one it would accept.
 */

import type { WorkforcePermission } from './types';

export const FORMULAS_PATH = '/api/v1/productivity/formulas';

export const formulaPath = (id: string): string => `${FORMULAS_PATH}/${id}`;

export const activateFormulaPath = (id: string): string => `${FORMULAS_PATH}/${id}/activate`;

/**
 * The versions of one formula key, newest first.
 *
 * `GET /productivity/formulas` is the catalogue of every key and every version;
 * narrowed by `formula_key` it is the lineage of one formula, which is the shape
 * `useVersionedConfig` models — see `FormulasScreen`'s comment on why the screen
 * switches between them.
 */
export const formulaKeyPath = (formulaKey: string): string =>
  `${FORMULAS_PATH}?formula_key=${encodeURIComponent(formulaKey)}`;

/** The code the catalogue and all four mutations are authorised on (`formulas/authorizer.ts`). */
export const FORMULA_PERMISSION: WorkforcePermission = 'workforce.manage_policies';

/**
 * The key the screen gives a company's first formula.
 *
 * Explicit rather than left to `keyFrom(name)`, which slugs the name: the default
 * formula's name is Vietnamese, and every letter outside `[a-z0-9]` becomes a hyphen,
 * so the derived key would be a row of hyphens nobody could type into `?formula_key=`.
 * Once a lineage exists the key is inherited from `based_on_formula_id` and this is
 * not sent at all.
 */
export const DEFAULT_FORMULA_KEY = 'productivity';

/* ─────────────────────────────────────────────────────────── the wire vocabulary */

/**
 * `FormulaComponentKey` — the nine components of Master §13.1, in the vocabulary's own
 * order (`src/modules/formulas/formula.ts`'s `FORMULA_COMPONENT_KEYS`).
 *
 * A closed set on the server too, and that is what makes a select the only honest
 * control: a component key is what the scorer dispatches on and what an explanation
 * shown to the person being judged is built from.
 */
export const FORMULA_COMPONENT_KEYS = [
  'task_result',
  'on_time',
  'quality',
  'productive_time',
  'focus',
  'attendance',
  'workload_balance',
  'verified_risk_penalty',
  'approved_manual_adjustment',
] as const;

export type FormulaComponentKey = (typeof FORMULA_COMPONENT_KEYS)[number];

/** Is this published key one of the nine this build still knows how to render? */
export function isFormulaComponentKey(value: string): value is FormulaComponentKey {
  return (FORMULA_COMPONENT_KEYS as readonly string[]).includes(value);
}

export type FormulaDirection = 'positive' | 'negative';

/**
 * Which way each component moves the score — a property of the **vocabulary**, not of
 * the request (`FORMULA_COMPONENT_DIRECTION` on the server, and `openapi.yaml` says
 * so at `FormulaComponent.direction`).
 *
 * The form never offers it. Stating the wrong one is refused by name, and a penalty
 * filed as positive would count toward the 100% *and* raise the score of whoever
 * collected the most verified findings.
 */
export const FORMULA_COMPONENT_DIRECTION: Readonly<Record<FormulaComponentKey, FormulaDirection>> =
  {
    task_result: 'positive',
    on_time: 'positive',
    quality: 'positive',
    productive_time: 'positive',
    focus: 'positive',
    attendance: 'positive',
    workload_balance: 'positive',
    approved_manual_adjustment: 'positive',
    verified_risk_penalty: 'negative',
  };

/** `wp_productivity_formulas.status`. Three values. */
export const FORMULA_STATUSES = ['draft', 'active', 'retired'] as const;

export type FormulaStatus = (typeof FORMULA_STATUSES)[number];

/** `FormulaScopeType` — the four rungs of the ladder, most specific first. */
export const FORMULA_SCOPE_TYPES = ['job_title', 'department', 'role', 'company'] as const;

export type FormulaScopeType = (typeof FORMULA_SCOPE_TYPES)[number];

/** `FormulaNormalize`. The form does not edit it; `DEFAULT_NORMALIZE` is what it sends. */
export type FormulaNormalize =
  | { readonly type: 'linear'; readonly min: number; readonly max: number }
  | { readonly type: 'none' };

/** The server's own default (`formula.ts`'s `DEFAULT_NORMALIZE`), sent explicitly. */
export const DEFAULT_NORMALIZE: FormulaNormalize = { type: 'linear', min: 0, max: 100 };

/* ───────────────────────────────────────────────────── the formula on the wire */

/** `RenderedFormulaComponent` — a component as it is published. */
export interface RenderedFormulaComponent {
  /**
   * Typed as a bare string and not as `FormulaComponentKey`, exactly as the contract
   * types it: a formula in use cannot be rewritten, so a key outside the current
   * vocabulary is still the key a March score was computed with. `known` is how the
   * server says which it is.
   */
  readonly key: string;
  readonly weight: number;
  readonly direction: FormulaDirection;
  readonly normalize: FormulaNormalize;
  readonly known: boolean;
}

/** `ProductivityFormula`, narrowed to what this screen reads. */
export interface ProductivityFormula {
  readonly id: string;
  readonly formula_key: string;
  readonly name: string;
  readonly description: string | null;
  readonly version: number;
  readonly status: FormulaStatus;
  readonly scope_type: FormulaScopeType;
  readonly scope_key: string | null;
  readonly components: readonly RenderedFormulaComponent[];
  /** The `NUMERIC(6,2)` total the server stored. Always `'100.00'` on a saved row. */
  readonly positive_weight_total: string;
  readonly effective_from: string;
  readonly effective_to: string | null;
  /** True once any score references this version. Freezes its arithmetic for ever. */
  readonly in_use: boolean;
  readonly locked_at: string | null;
  readonly activated_at: string | null;
  readonly retired_at: string | null;
  readonly supersedes_id: string | null;
  readonly created_at: string;
}

export interface ProductivityFormulaListResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly data: readonly ProductivityFormula[];
}

/** One term as a **request** carries it: `FormulaComponent`, minus what is derived. */
export interface FormulaComponentInput {
  readonly key: FormulaComponentKey;
  readonly weight: number;
  readonly normalize: FormulaNormalize;
}

/**
 * The keys the form may touch, and therefore the keys a `PATCH` body may carry.
 *
 * A strict subset of `ProductivityFormulaPatchRequest`, which is
 * `additionalProperties: false`. `based_on_formula_id` and `activate` are absent on
 * purpose — both are create-only, and a draft holding either would put it into the
 * next patch. `scope_type`/`scope_key` are absent because the patch schema moves them
 * together or not at all, and a form that offered one without the other would earn a
 * refusal naming a field nobody touched; a version at another rung is created, not
 * edited into place.
 */
export interface ProductivityFormulaDraft {
  readonly name: string;
  readonly description: string | null;
  readonly components: readonly FormulaComponentInput[];
}

/** `ProductivityFormulaCreateRequest`. Two members are required; the rest have defaults. */
export interface ProductivityFormulaCreateRequest {
  readonly name: string;
  readonly components: readonly FormulaComponentInput[];
  readonly description?: string | null;
  readonly formula_key?: string;
  readonly based_on_formula_id?: string | null;
  /** Puts the new version into force in the same transaction. Defaults to `false`. */
  readonly activate?: boolean;
}

/* ─────────────────────────────────────────────────────────── the weight arithmetic */

/** Master §13.2's rule, in the unit the column uses: percentage points. */
export const REQUIRED_POSITIVE_TOTAL = 100;

/**
 * One weight as an integer number of hundredths, or `null` when it has more than two
 * decimal places.
 *
 * The server's `weightHundredths` in the browser, and deliberately the same
 * arithmetic: `28.4 + 35.8 + 35.8` is `99.99999999999999` in JavaScript and
 * `28.1 + 35.95 + 35.95` is `100.00000000000001`, and both are perfectly ordinary
 * correct formulas. Summing floats and comparing to `100` rejects the first; widening
 * the tolerance enough to accept it also accepts a formula that really does add up to
 * 99.99999%. So every weight becomes an integer at the boundary and the total is
 * compared against `10000` (`DATA_MODEL.md` §6395).
 */
export function weightHundredths(weight: number): number | null {
  if (!Number.isFinite(weight)) return null;
  const scaled = Math.round(weight * 100);
  return Math.abs(scaled - weight * 100) > 1e-6 ? null : scaled;
}

/** Why one weight is inadmissible, in the server's own words, or `null`. */
export type WeightProblem = 'not_finite' | 'negative' | 'above_100' | 'too_many_decimals';

export function weightProblem(weight: number): WeightProblem | null {
  if (!Number.isFinite(weight)) return 'not_finite';
  if (weight < 0) return 'negative';
  if (weight > 100) return 'above_100';
  if (weightHundredths(weight) === null) return 'too_many_decimals';
  return null;
}

/**
 * The total of the **positive** weights, in hundredths, or `null` when any positive
 * weight is inadmissible and there is therefore no total to state.
 *
 * A negative component is not part of the sum: it is subtracted on top.
 */
export function positiveWeightHundredths(
  components: readonly FormulaComponentInput[],
): number | null {
  let total = 0;
  for (const component of components) {
    if (FORMULA_COMPONENT_DIRECTION[component.key] !== 'positive') continue;
    const hundredths = weightHundredths(component.weight);
    if (hundredths === null) return null;
    total += hundredths;
  }
  return total;
}

/** `'100.00'` — a hundredths total in the shape the column and the API print it. */
export function formatHundredths(hundredths: number): string {
  const sign = hundredths < 0 ? '-' : '';
  const value = Math.abs(hundredths);
  return `${sign}${String(Math.trunc(value / 100))}.${String(value % 100).padStart(2, '0')}`;
}

/**
 * Every reason this component set would be refused, in the order the server checks
 * them — shape first, then the sum, because "the total is 66.67" is a misleading thing
 * to say about a set whose third weight has six decimal places.
 *
 * Empty means the server would accept it. This is not the gate — the server is, twice
 * over — it is the round trip the person does not have to make.
 */
export type ComponentSetProblem =
  | { readonly kind: 'empty' }
  | { readonly kind: 'weight'; readonly key: FormulaComponentKey; readonly problem: WeightProblem }
  /** A **positive** component weighted `0`: remove it rather than listing it at zero. */
  | { readonly kind: 'zero_positive'; readonly key: FormulaComponentKey }
  /** The positive weights do not sum to `100.00`. `total` is in hundredths. */
  | { readonly kind: 'sum'; readonly total: number };

export function componentSetProblems(
  components: readonly FormulaComponentInput[],
): readonly ComponentSetProblem[] {
  const problems: ComponentSetProblem[] = [];
  if (components.length === 0) problems.push({ kind: 'empty' });

  for (const component of components) {
    const problem = weightProblem(component.weight);
    if (problem !== null) {
      problems.push({ kind: 'weight', key: component.key, problem });
      continue;
    }
    if (
      FORMULA_COMPONENT_DIRECTION[component.key] === 'positive' &&
      weightHundredths(component.weight) === 0
    ) {
      problems.push({ kind: 'zero_positive', key: component.key });
    }
  }
  if (problems.length > 0) return problems;

  const total = positiveWeightHundredths(components);
  if (total !== REQUIRED_POSITIVE_TOTAL * 100) {
    problems.push({ kind: 'sum', total: total ?? 0 });
  }
  return problems;
}

/* ─────────────────────────────────────────────── the default the empty state names */

/**
 * Master §13.2's own sample formula — six positive terms summing to 100 and the
 * verified-risk penalty on top.
 *
 * **Nothing seeds this, and the empty state says so rather than implying a seed.**
 * `migrations/017_formulas.sql` creates `wp_productivity_formulas` and inserts no row;
 * no migration, script or bootstrap writes one for a new company. So a company with no
 * formula scores nobody — `GET /productivity/formulas/effective` answers `outcome:
 * unconfigured`, never a built-in default and never a zero — and the useful thing an
 * empty screen can do is name the formula the spec describes and offer to create it,
 * which is what this is for.
 */
export const DEFAULT_FORMULA_COMPONENTS: readonly FormulaComponentInput[] = [
  { key: 'task_result', weight: 25, normalize: DEFAULT_NORMALIZE },
  { key: 'on_time', weight: 15, normalize: DEFAULT_NORMALIZE },
  { key: 'quality', weight: 10, normalize: DEFAULT_NORMALIZE },
  { key: 'productive_time', weight: 20, normalize: DEFAULT_NORMALIZE },
  { key: 'focus', weight: 15, normalize: DEFAULT_NORMALIZE },
  { key: 'attendance', weight: 15, normalize: DEFAULT_NORMALIZE },
  { key: 'verified_risk_penalty', weight: 10, normalize: DEFAULT_NORMALIZE },
];

/**
 * A published version's components as a **request** carries them.
 *
 * The bridge between the two shapes, and the one place it happens: `direction` and
 * `known` are derived by the server and refused if restated wrongly, so they are
 * dropped here rather than round-tripped. A component whose key is outside the
 * vocabulary is dropped too — it cannot be sent back, because `FormulaComponentKey`
 * is a closed enum on the wire.
 *
 * **A dropped component is a hole in the sum, and the screen has to say so.** Its
 * weight leaves with it, so branching from a version that carries one produces a
 * positive total below 100 and an inline refusal blaming weights the person never
 * touched. `droppedComponents` is what the screen renders to name the component that
 * went, on the row and in the composer, so that a legacy formula is redistributable
 * rather than merely unsendable.
 */
export function componentInputsOf(formula: ProductivityFormula): readonly FormulaComponentInput[] {
  const known: FormulaComponentInput[] = [];
  for (const component of formula.components) {
    if (!isFormulaComponentKey(component.key)) continue;
    known.push({
      key: component.key,
      weight: component.weight,
      normalize: component.normalize,
    });
  }
  return known;
}

/**
 * The components this version carries that a request cannot carry back.
 *
 * `known: false` is the server's own word for it (`src/modules/formulas/service.ts`
 * publishes the flag precisely so a client can say which component it is), and the
 * key test is repeated here because the two must not be able to disagree: anything
 * `componentInputsOf` drops must appear in this list, or the screen would explain a
 * shortfall it had not accounted for.
 *
 * A version in force may legitimately carry one: the vocabulary is a closed enum
 * *now*, and a version frozen against March's scores was written against March's
 * vocabulary. It cannot be rewritten and it is not wrong; it simply cannot be
 * resent unchanged.
 */
export function droppedComponents(
  formula: ProductivityFormula,
): readonly RenderedFormulaComponent[] {
  return formula.components.filter(
    (component) => !component.known || !isFormulaComponentKey(component.key),
  );
}

/**
 * May this version be edited in place?
 *
 * Both halves of `patchDraftFormula`'s refusal, checked here so the control is absent
 * rather than present-and-refused: `in_use` is `409 WORKFORCE_FORMULA_IMMUTABLE` (the
 * trigger `wp_formulas_immutable` refuses the UPDATE underneath the service), and any
 * status but `draft` is `409 WORKFORCE_INVALID_STATE_TRANSITION` — an active version
 * is not frozen for ever, it is simply never edited in place, because an edit would
 * change what applies to people with no new version for anybody to point at.
 *
 * **This is not a precondition.** It travels as a screen decision about which control
 * to render, not as `preconditionOf` returning `null`, because `null` means "the token
 * is missing" and on this backend there is no token to miss.
 */
export function isEditable(formula: ProductivityFormula): boolean {
  return formula.status === 'draft' && !formula.in_use;
}

/** May this version be put into force? Only a draft; `activateFormulaVersion` says so. */
export function isActivatable(formula: ProductivityFormula): boolean {
  return formula.status === 'draft';
}
